import { supabase } from '@/lib/supabaseClient';
import { updateJob, formatUsd, parseContractorDate, computeEmbeddedJobMargin } from '@/lib/jobsData';
import * as pqi from '@/lib/pqiTracker';
import { loadCrewRoster } from '@/lib/crewRoster';

export { formatUsd };

/* ----------------------------------------------------------------------------
 * Format helpers
 * ------------------------------------------------------------------------- */
export const formatPct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0%';
  return `${(n).toFixed(1)}%`;
};

export const marginTone = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return 'danger';
  if (n >= 30) return 'success';
  if (n >= 15) return 'warning';
  return 'danger';
};

export const marginLabel = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return 'Unknown';
  if (n >= 30) return 'Healthy';
  if (n >= 15) return 'Watch';
  if (n >= 0) return 'At risk';
  return 'Loss';
};

/* ----------------------------------------------------------------------------
 * Fetch + join
 * ------------------------------------------------------------------------- */
const JOB_FIELDS =
  'id,name,client,client_id,address,contract,co_total,status,phase,job_type,materials_spent,material_log,receipts,invoices,change_orders,created_at';

const TE_COSTING_FIELDS = 'id,crew_id,name,job,date,hours,cost,created_at';

const isMissingColumn = (error) =>
  !!error &&
  (error.code === '42703' ||
    /does not exist|could not find the/i.test(error.message || ''));

/**
 * Time entries with worker_type ('crew' | 'sub', stamped at log time by
 * migration 084). Until that migration is applied the column doesn't exist,
 * so fall back to the plain select and classify each entry from the CURRENT
 * roster — right for today, the stamped column is what keeps history exact.
 */
const fetchCostingTimeEntries = async () => {
  const withType = await supabase
    .from('time_entries')
    .select(`${TE_COSTING_FIELDS},worker_type`)
    .order('date', { ascending: false });
  if (!withType.error) return withType.data || [];
  if (!isMissingColumn(withType.error)) throw withType.error;
  const plain = await supabase
    .from('time_entries')
    .select(TE_COSTING_FIELDS)
    .order('date', { ascending: false });
  if (plain.error) throw plain.error;
  let roster = [];
  try {
    roster = await loadCrewRoster();
  } catch {
    roster = [];
  }
  const typeById = new Map(roster.map((m) => [m.id, m.type]));
  return (plain.data || []).map((e) => ({
    ...e,
    worker_type: typeById.get(e.crew_id) === 'sub' ? 'sub' : 'crew',
  }));
};

export const fetchCostingData = async () => {
  const [jobsRes, timeEntries, estRes] = await Promise.all([
    supabase.from('jobs').select(JOB_FIELDS).order('created_at', { ascending: false }),
    fetchCostingTimeEntries(),
    supabase
      .from('estimates')
      .select('id,job_id,title,total,status,approved_at,created_at')
      .order('created_at', { ascending: false }),
  ]);
  if (jobsRes.error) throw jobsRes.error;
  if (estRes.error) throw estRes.error;
  return {
    jobs: jobsRes.data || [],
    timeEntries,
    estimates: estRes.data || [],
  };
};

// `hours`/`cost` stay the job's TOTAL labor (a sub's hourly cost is real
// job cost, so margin math is unchanged); `sub` breaks out the part paid to
// subcontractors so it isn't mistaken for crew productivity.
const computeLaborForJob = (job, timeEntries = []) => {
  const name = (job?.name || '').trim().toLowerCase();
  let totalHours = 0;
  let totalCost = 0;
  let subHours = 0;
  let subCost = 0;
  const entries = [];
  for (const e of timeEntries) {
    if (!e) continue;
    const eName = (e.job || '').trim().toLowerCase();
    if (eName !== name) continue;
    const h = Number(e.hours) || 0;
    const c = Number(e.cost) || 0;
    totalHours += h;
    totalCost += c;
    if (e.worker_type === 'sub') {
      subHours += h;
      subCost += c;
    }
    entries.push(e);
  }
  return {
    hours: totalHours,
    cost: totalCost,
    crew: { hours: totalHours - subHours, cost: totalCost - subCost },
    sub: { hours: subHours, cost: subCost },
    entries,
  };
};

const computeMaterialsForJob = (job) => {
  const log = Array.isArray(job?.material_log) ? job.material_log : [];
  const logged = log.reduce((sum, row) => sum + (Number(row?.amount) || 0), 0);
  const bulk = Number(job?.materials_spent) || 0;
  // Prefer logged total when present; otherwise fall back to bulk.
  const total = logged > 0 ? logged : bulk;
  return { logged, bulk, total, entries: log };
};

const findEstimateForJob = (jobId, estimates = []) => {
  if (!jobId) return null;
  // Phase 27 — prefer a Won (or legacy 'approved') estimate linked to this job.
  // Migration 014 backfills 'approved' → 'won' so both should never coexist
  // post-migration; the OR keeps pre-migration data working.
  const winner = estimates.find(
    (e) =>
      e?.job_id === jobId &&
      (e?.status === 'won' || e?.status === 'approved')
  );
  if (winner) return winner;
  return estimates.find((e) => e?.job_id === jobId) || null;
};

/**
 * Join the raw data into per-job costing rows.
 */
export const buildCostingRows = ({ jobs = [], timeEntries = [], estimates = [] } = {}) => {
  return jobs.map((job) => {
    const labor = computeLaborForJob(job, timeEntries);
    const materials = computeMaterialsForJob(job);
    const actualCost = labor.cost + materials.total;
    const contract = Number(job?.contract) || 0;
    const coTotal = Number(job?.co_total) || 0;
    // T&M jobs have no contract to measure against — "revenue" is what has
    // been BILLED to date (invoice total), the stored contract figure is
    // usually just the deposit/retainer, and "% of budget" is meaningless.
    // Mid-billing-cycle a T&M job routinely shows cost ahead of billed; that
    // is unbilled work, not a loss (the Jennison lesson, Sep 5).
    const isTM = (job?.job_type || 'fixed') === 'tm';
    const invoicesArr = Array.isArray(job?.invoices) ? job.invoices : [];
    const billed = invoicesArr.reduce((s, i) => s + (Number(i?.amount) || 0), 0);
    const revenue = isTM ? billed : contract + coTotal;
    const estimate = findEstimateForJob(job.id, estimates);
    const budget = isTM ? billed : estimate?.total ? Number(estimate.total) : revenue;
    const grossProfit = revenue - actualCost;
    const marginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
    const budgetVariance = budget - actualCost;
    const budgetVariancePct = budget > 0 ? (budgetVariance / budget) * 100 : 0;
    return {
      is_tm: isTM,
      billed,
      job,
      job_id: job.id,
      name: job.name || 'Untitled job',
      client: job.client || '',
      address: job.address || '',
      status: job.status || '',
      phase: Number(job.phase) || 0,
      contract,
      co_total: coTotal,
      revenue,
      estimate,
      budget,
      labor,
      materials,
      actualCost,
      grossProfit,
      marginPct,
      budgetVariance,
      budgetVariancePct,
      tone: marginTone(marginPct),
    };
  });
};

/* ----------------------------------------------------------------------------
 * Overview stats
 * ------------------------------------------------------------------------- */
export const computeOverviewStats = (rows = []) => {
  let revenue = 0;
  let actualCost = 0;
  let laborCost = 0;
  let materialsCost = 0;
  let budget = 0;
  let healthy = 0;
  let watch = 0;
  let risk = 0;
  for (const r of rows) {
    revenue += r.revenue;
    actualCost += r.actualCost;
    laborCost += r.labor.cost;
    materialsCost += r.materials.total;
    budget += r.budget;
    // T&M rows don't get health-scored — billed lags work mid-cycle, so a
    // fixed-price margin label would cry wolf on a normal T&M state.
    if (r.is_tm) continue;
    if (r.tone === 'success') healthy += 1;
    else if (r.tone === 'warning') watch += 1;
    else risk += 1;
  }
  const grossProfit = revenue - actualCost;
  const marginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
  return {
    count: rows.length,
    revenue,
    actualCost,
    laborCost,
    materialsCost,
    budget,
    grossProfit,
    marginPct,
    healthy,
    watch,
    risk,
  };
};

/* ----------------------------------------------------------------------------
 * Filter + sort
 * ------------------------------------------------------------------------- */
export const applyCostingFilter = (
  rows = [],
  { search = '', statusFilter = 'all', sort = 'margin_asc' } = {}
) => {
  const q = (search || '').trim().toLowerCase();
  let filtered = rows.filter((r) => {
    if (statusFilter === 'active' && r.status !== 'active') return false;
    if (statusFilter === 'closed' && r.status !== 'closed') return false;
    if (statusFilter === 'healthy' && r.tone !== 'success') return false;
    if (statusFilter === 'watch' && r.tone !== 'warning') return false;
    if (statusFilter === 'risk' && r.tone !== 'danger') return false;
    if (!q) return true;
    const hay = [r.name, r.client, r.address].map((v) => (v || '').toLowerCase()).join(' | ');
    return hay.includes(q);
  });
  switch (sort) {
    case 'margin_desc':
      filtered = filtered.slice().sort((a, b) => b.marginPct - a.marginPct);
      break;
    case 'revenue_desc':
      filtered = filtered.slice().sort((a, b) => b.revenue - a.revenue);
      break;
    case 'actual_desc':
      filtered = filtered.slice().sort((a, b) => b.actualCost - a.actualCost);
      break;
    case 'name_az':
      filtered = filtered.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      break;
    case 'margin_asc':
    default:
      filtered = filtered.slice().sort((a, b) => a.marginPct - b.marginPct);
      break;
  }
  return filtered;
};

/* ----------------------------------------------------------------------------
 * Material log writes
 * ------------------------------------------------------------------------- */
const genId = (prefix) => {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().split('-')[0].toUpperCase()
      : Math.random().toString(36).slice(2, 8).toUpperCase();
  return `${prefix}-${id}`;
};

export const logMaterialCost = async (
  job,
  { store = '', desc = '', amount = 0, date = '' } = {}
) => {
  if (!job?.id) throw new Error('Missing job id');
  const entry = {
    id: genId('MAT'),
    store: String(store || 'Other'),
    desc: String(desc || '').slice(0, 200),
    amount: Number(amount) || 0,
    date: date || new Date().toISOString().slice(0, 10),
    created_at: new Date().toISOString(),
  };
  const log = [entry, ...(Array.isArray(job.material_log) ? job.material_log : [])];
  const materials_spent = log.reduce((s, r) => s + (Number(r?.amount) || 0), 0);
  const updated = await updateJob(job.id, { material_log: log, materials_spent });
  // PQI: track material cost (silent)
  pqi.trackMaterialLogged({
    job,
    store: entry.store,
    amount: entry.amount,
    category: entry.store,
  });
  // PQI: margin snapshot — cost basis just changed
  {
    const { actualCost, totalRevenue, marginPct } = computeEmbeddedJobMargin(updated);
    pqi.trackMarginSnapshot({
      job: updated,
      actualCost,
      totalRevenue,
      marginPct,
      trigger: 'material_logged',
    });
  }
  return updated;
};

export const deleteMaterialLogEntry = async (job, entryId) => {
  if (!job?.id) throw new Error('Missing job id');
  const log = (Array.isArray(job.material_log) ? job.material_log : []).filter(
    (r) => r?.id !== entryId
  );
  const materials_spent = log.reduce((s, r) => s + (Number(r?.amount) || 0), 0);
  return updateJob(job.id, { material_log: log, materials_spent });
};

/* ----------------------------------------------------------------------------
 * CSV export
 * ------------------------------------------------------------------------- */
export const rowsToCsv = (rows = []) => {
  const header = [
    'Job',
    'Client',
    'Status',
    'Contract',
    'CO Total',
    'Revenue',
    'Budget',
    'Labor Hrs',
    'Labor Cost',
    'Sub Labor Cost',
    'Materials Cost',
    'Actual Cost',
    'Gross Profit',
    'Margin %',
    'Budget Variance',
  ];
  const esc = (v) => {
    const s = String(v == null ? '' : v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push(
      [
        r.name,
        r.client,
        r.status,
        r.contract,
        r.co_total,
        r.revenue,
        r.budget,
        r.labor.hours.toFixed(2),
        r.labor.cost.toFixed(2),
        (r.labor.sub?.cost || 0).toFixed(2),
        r.materials.total.toFixed(2),
        r.actualCost.toFixed(2),
        r.grossProfit.toFixed(2),
        r.marginPct.toFixed(2),
        r.budgetVariance.toFixed(2),
      ]
        .map(esc)
        .join(',')
    );
  }
  return lines.join('\n');
};

export const downloadCsv = (filename, csvContent) => {
  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    // noop
  }
};

/* ----------------------------------------------------------------------------
 * Helpers for detail view
 * ------------------------------------------------------------------------- */
export const groupLaborByDate = (entries = []) => {
  const groups = new Map();
  for (const e of entries) {
    const key = e?.date || 'unknown';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  }
  const list = [];
  for (const [date, es] of groups.entries()) {
    list.push({
      date,
      entries: es,
      hours: es.reduce((s, e) => s + (Number(e?.hours) || 0), 0),
      cost: es.reduce((s, e) => s + (Number(e?.cost) || 0), 0),
    });
  }
  list.sort((a, b) => {
    const da = parseContractorDate(a.date)?.getTime() ?? 0;
    const db = parseContractorDate(b.date)?.getTime() ?? 0;
    return db - da;
  });
  return list;
};
